import { assignmentIds, priorityKey, stage } from './taskFilters.js'

const progress = { pending: 0, active: 50, completed: 100 }
const priorityValues = { urgent: 1, important: 3, medium: 5, low: 9 }
const assignment = { '@odata.type': 'microsoft.graph.plannerAssignment', orderHint: ' !' }
const same = { skip: 'Al zo ingesteld' }
const labelKey = (board, name) => Object.entries(board.labels || {}).find(([, label]) => label?.trim() === name)?.[0]
const after = (key, prefix) => key.slice(prefix.length)

// Column keys come from PlannerColumns. A task can sit in several person or label columns, so the source column decides what it leaves.
export function moveChange(task, groupBy, fromKey, toKey) {
  if (!task['@odata.etag']) return { skip: 'Ververs het overzicht eerst' }
  if (fromKey === toKey) return same
  switch (groupBy) {
    case 'status':
      return stage(task) === toKey ? same : { changes: { percentComplete: progress[toKey] } }
    case 'priority':
      return priorityKey(task.priority ?? 5) === toKey ? same : { changes: { priority: priorityValues[toKey] } }
    case 'person': {
      const ids = assignmentIds(task), from = after(fromKey, 'person:'), to = after(toKey, 'person:')
      const assignments = {}
      if (to === 'none') ids.forEach(id => { assignments[id] = null })
      else {
        if (from !== 'none' && ids.includes(from)) assignments[from] = null
        if (!ids.includes(to)) assignments[to] = assignment
      }
      return Object.keys(assignments).length ? { changes: { assignments } } : same
    }
    case 'label': {
      const applied = Object.keys(task.appliedCategories || {}).filter(key => task.appliedCategories[key] === true)
      const from = after(fromKey, 'label:'), to = after(toKey, 'label:')
      const categories = {}
      if (to === 'none') applied.forEach(key => { categories[key] = false })
      else {
        const target = labelKey(task.board, to)
        if (!target) return { skip: 'Dit bord heeft dit label niet' }
        const source = from === 'none' ? null : labelKey(task.board, from)
        if (source && source !== target && applied.includes(source)) categories[source] = false
        if (!applied.includes(target)) categories[target] = true
      }
      return Object.keys(categories).length ? { changes: { appliedCategories: categories } } : same
    }
    case 'plan':
      return { skip: 'Een taak kan niet naar een ander bronbord' }
    default: {
      const [planId, bucketId] = toKey.split(':')
      if (planId !== task.board.id) return { skip: 'Alleen buckets van hetzelfde bronbord' }
      return bucketId === task.bucketId ? same : { changes: { bucketId } }
    }
  }
}

// A task created in a column starts with that column's value. Plan and bucket columns fix the board; other columns use the chosen board.
export function newTaskFields(groupBy, key, board) {
  switch (groupBy) {
    case 'status':
      return { planId: board.id, fields: progress[key] ? { percentComplete: progress[key] } : {} }
    case 'priority':
      return { planId: board.id, fields: { priority: priorityValues[key] } }
    case 'person': {
      const id = after(key, 'person:')
      return { planId: board.id, fields: id === 'none' ? {} : { assignments: { [id]: assignment } } }
    }
    case 'label': {
      const name = after(key, 'label:')
      if (name === 'none') return { planId: board.id, fields: {} }
      const category = labelKey(board, name)
      return category ? { planId: board.id, fields: { appliedCategories: { [category]: true } } } : { skip: 'Dit bord heeft dit label niet' }
    }
    case 'plan':
      return { planId: key, fields: {} }
    default: {
      const [planId, bucketId] = key.split(':')
      return { planId, bucketId, fields: {} }
    }
  }
}
